/**
 * BureauActivityTab - Admin-only tab for monitoring bureau user activity
 */

import React, { useState, useEffect } from 'react';
import './BureauActivityTab.css';
import {
  ChartBarIcon,
  Bars3BottomLeftIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';
import { ActivityStats } from './components/ActivityStats';
import { ActivityTimeline } from './components/ActivityTimeline';
import { UserSummaryTable } from './components/UserSummaryTable';
import { UserDetailPanel } from './components/UserDetailPanel';
import {
  useBureauUsers,
  useBureauActivity,
  useBureauStats,
  useBureauSummary,
  useUserActivityDetails,
  useDateRange,
  useAllTeamPlanning
} from './hooks/useBureauActivity';
import { bureauActivityAPI, type TodoCountEntry } from './api/bureau-activity.api';
import { ViewMode } from './types/bureau-activity.types';

export const BureauActivityTab: React.FC = () => {
  const [viewMode, setViewMode] = useState<ViewMode>('timeline');
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [activePreset, setActivePreset] = useState<string>('today');

  // Date range management
  const { dateRange, setDateRange, setPreset } = useDateRange();

  // Data fetching
  const { users, loading: usersLoading } = useBureauUsers();
  const { stats, loading: statsLoading } = useBureauStats(dateRange);
  const { activities, loading: activitiesLoading } = useBureauActivity(
    dateRange,
    selectedUserId || undefined
  );
  const { summary, loading: summaryLoading } = useBureauSummary(dateRange);
  const { details: userDetails, loading: detailsLoading } = useUserActivityDetails(
    selectedUserId || null,
    dateRange
  );
  const { getPlanningForUserDate } = useAllTeamPlanning(dateRange);

  // Tracker à faire counts per user
  const [todoCounts, setTodoCounts] = useState<Record<string, TodoCountEntry> | undefined>();
  useEffect(() => {
    bureauActivityAPI.getTodoCounts()
      .then(res => { if (res.success) setTodoCounts(res.data); })
      .catch(() => {});
  }, []);

  // Get selected user object
  const selectedUser = users.find(u => u.id === selectedUserId);

  // Handle preset button click
  const handlePreset = (preset: 'today' | 'week' | 'month' | 'quarter') => {
    setPreset(preset);
    setActivePreset(preset);
  };

  // Handle date input change
  const handleDateChange = (field: 'startDate' | 'endDate', value: string) => {
    setDateRange({ ...dateRange, [field]: value });
    setActivePreset(''); // Clear preset when manually changing dates
  };

  // Handle user selection from summary table
  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setViewMode('timeline');
  };

  return (
    <div className="bureau-activity-tab">
      {/* Header */}
      <div className="bureau-activity-header">
        <div className="bureau-activity-title">
          <ChartBarIcon className="icon" />
          <h1>Activité Bureau</h1>
        </div>

        {/* Filters */}
        <div className="bureau-activity-filters">
          {/* User filter */}
          <div className="filter-group">
            <label>Utilisateur:</label>
            <select
              value={selectedUserId}
              onChange={(e) => setSelectedUserId(e.target.value)}
              disabled={usersLoading}
            >
              <option value="">Tous les utilisateurs</option>
              {users.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.name}
                </option>
              ))}
            </select>
          </div>

          {/* Date range */}
          <div className="filter-group">
            <label>Du:</label>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => handleDateChange('startDate', e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Au:</label>
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => handleDateChange('endDate', e.target.value)}
            />
          </div>

          {/* Quick presets */}
          <div className="date-presets">
            <button
              className={`date-preset-btn ${activePreset === 'today' ? 'active' : ''}`}
              onClick={() => handlePreset('today')}
            >
              Aujourd'hui
            </button>
            <button
              className={`date-preset-btn ${activePreset === 'week' ? 'active' : ''}`}
              onClick={() => handlePreset('week')}
            >
              7 jours
            </button>
            <button
              className={`date-preset-btn ${activePreset === 'month' ? 'active' : ''}`}
              onClick={() => handlePreset('month')}
            >
              30 jours
            </button>
            <button
              className={`date-preset-btn ${activePreset === 'quarter' ? 'active' : ''}`}
              onClick={() => handlePreset('quarter')}
            >
              90 jours
            </button>
          </div>

          {/* View toggle */}
          <div className="view-toggle">
            <button
              className={`view-toggle-btn ${viewMode === 'timeline' ? 'active' : ''}`}
              onClick={() => setViewMode('timeline')}
              title="Timeline"
            >
              <Bars3BottomLeftIcon className="w-5 h-5" />
            </button>
            <button
              className={`view-toggle-btn ${viewMode === 'summary' ? 'active' : ''}`}
              onClick={() => setViewMode('summary')}
              title="Résumé"
            >
              <TableCellsIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bureau-activity-content">
        {/* Stats cards */}
        <ActivityStats stats={stats} loading={statsLoading} />

        {/* Main content based on view mode */}
        {viewMode === 'timeline' && (
          <div className="activity-section">
            <div className="activity-section-header">
              <div className="activity-section-title-row">
                <h2 className="activity-section-title">Timeline d'activité</h2>
                {selectedUserId && userDetails && (
                  <span className="actions-badge">
                    {userDetails.actionCount} actions distinctes
                  </span>
                )}
              </div>
              {selectedUserId && (
                <button
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => setSelectedUserId('')}
                >
                  Voir tous les utilisateurs
                </button>
              )}
            </div>
            {activitiesLoading ? (
              <div className="loading-spinner">
                <div className="spinner" />
              </div>
            ) : (
              <ActivityTimeline
                activities={activities}
                selectedUserId={selectedUserId || undefined}
                onUserSelect={setSelectedUserId}
                getPlanningForUserDate={getPlanningForUserDate}
                todoCounts={todoCounts}
              />
            )}
          </div>
        )}

        {viewMode === 'summary' && (
          <div className="activity-section">
            <div className="activity-section-header">
              <h2 className="activity-section-title">Résumé par utilisateur</h2>
            </div>
            <UserSummaryTable
              summary={summary}
              loading={summaryLoading}
              onUserSelect={handleUserSelect}
              todoCounts={todoCounts}
            />
          </div>
        )}

        {/* User detail panel - shown when a specific user is selected (after timeline) */}
        {selectedUserId && selectedUser && (
          <UserDetailPanel
            user={selectedUser}
            details={userDetails}
            loading={detailsLoading}
            onClose={() => setSelectedUserId('')}
          />
        )}
      </div>
    </div>
  );
};
